import { type Component, createSignal, onMount, onCleanup, Show } from 'solid-js';
import { useTranslation } from '@/i18n';

const OfflineDetector: Component = () => {
  const { t } = useTranslation();
  const [isOffline, setIsOffline] = createSignal(!navigator.onLine);
  const [wasOffline, setWasOffline] = createSignal(false);
  const [showReconnected, setShowReconnected] = createSignal(false);

  onMount(() => {
    const handleOnline = () => {
      setIsOffline(false);
      if (wasOffline()) {
        setShowReconnected(true);
        // Hide reconnected message after 3 seconds
        setTimeout(() => setShowReconnected(false), 3000);
      }
      setWasOffline(false);
    };

    const handleOffline = () => {
      setIsOffline(true);
      setWasOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    onCleanup(() => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    });
  });

  return (
    <>
      {/* Offline Banner */}
      <Show when={isOffline()}>
        <div
          class="fixed top-0 left-0 right-0 z-[100] bg-red-600 text-white py-2 px-4 text-center animate-slide-down"
          role="alert"
          aria-live="assertive"
        >
          <div class="flex items-center justify-center gap-2">
            <span class="text-xl animate-pulse">📡</span>
            <span class="font-medium">{t('errors.networkError')}</span>
          </div>
          <p class="text-sm text-red-100 mt-1">
            {t('offline.checkConnection')}
          </p>
        </div>
      </Show>

      {/* Reconnected Toast */}
      <Show when={showReconnected()}>
        <div
          class="fixed top-4 left-1/2 transform -translate-x-1/2 z-[100] bg-emerald-600 text-white py-2 px-4 rounded-full shadow-lg animate-fade-in"
          role="status"
          aria-live="polite"
        >
          <div class="flex items-center gap-2">
            <span>✅</span>
            <span class="font-medium">{t('notifications.connectionRestored')}</span>
          </div>
        </div>
      </Show>

      <style>{`
        @keyframes slide-down {
          from {
            transform: translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .animate-slide-down {
          animation: slide-down 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default OfflineDetector;
